import {DateTime} from 'luxon';

/**
 * Recursively converts ISO date strings back into Luxon DateTime objects.
 * Only strings that pass a round-trip check are converted (DateTime.fromISO(value).toISO() === value).
 * Does not create new objects or arrays that don't contain dates - preserves original references.
 * @param value The value to process (object, array, or primitive).
 * @returns The transformed value with DateTime objects restored.
 */

export default function deserializeDates<T>(value: T): T {
  if (typeof value === 'string' && DateTime.fromISO(value).toISO() === value) {
    return DateTime.fromISO(value) as unknown as T; // Convert valid ISO string to DateTime
  }

  if (Array.isArray(value)) {
    let didChange = false;
    const deserializedArray: unknown[] = [];

    for (let i = 0; i < value.length; i++) {
      const item = value[i];
      const deserializedItem = deserializeDates(item);

      if (deserializedItem !== item) {
        didChange = true;
      }

      deserializedArray[i] = deserializedItem;
    }

    return (didChange ? deserializedArray : value) as T;
  }

  if (value !== null && typeof value === 'object') {
    let didChange = false;
    const deserializedObject: Record<string, unknown> = {};

    for (const [key, val] of Object.entries(value)) {
      const deserializedVal = deserializeDates(val);

      if (deserializedVal !== val) {
        didChange = true;
      }

      deserializedObject[key] = deserializedVal;
    }

    return (didChange ? deserializedObject : value) as T;
  }

  return value;
}
